import { ActionTree } from "vuex";
import * as types from "@webparts/store/types";
import calendarApi from "@webparts/components/webparts/YASKAWA/Calendar/api";
import {
    BirthdayEmployeeInfo,
    CalendarEventResponseData,
    CalendarRequestData,
    CompanyHoliday,
    EwsCalendarFolder,
    EwsRequest,
} from "@webparts/components/webparts/YASKAWA/Calendar/types";
import { WebpartDataType } from "../types/enums";
import { IEDate, uGetDateFormat, uGetDaysInMonth } from "@appbase/util";

import accountStore from "@account/store";

const getEventRequest = function (start: Date, end: Date, folderIds: string[]): CalendarRequestData {
    return {
        EwsFolderIDArray: folderIds,
        StartDate: uGetDateFormat("yyyy-MM-dd", start),
        EndDate: uGetDateFormat("yyyy-MM-dd", end),
        Status: null,
        Subject: null,
        Location: null,
        OrganizerName: null,
        OrganizerAddress: null,
        PersonCode: "",
        RequiredAttendees: null,
        OptionalAttendees: null,
        EumNoCondition: false,
        EumPageNumber: 1,
        EumRowsPerPage: 9999,
    };
};

export default <ActionTree<webpartv3.state.WebpartsState, RootState>>{
    async [types.WEBPART_COMMONDATA](action, payload: WebpartDataType[]) {
        action.commit(types.WEBPART_COMMONDATA_MOUNTED, false);

        let pUnreadMail = payload.indexOf(WebpartDataType.UnreadMail) >= 0;
        let pDailyEvent = payload.indexOf(WebpartDataType.DailyEvent) >= 0;
        let pMonthlyEvent = payload.indexOf(WebpartDataType.MonthlyEvent) >= 0;

        if (pUnreadMail) {
            await action.dispatch(types.WEBPART_UNREADMAIL_DATA);
        }

            if (pDailyEvent || pMonthlyEvent) {
                calendarApi.getCalendars().then(async (folderRes) => {
                    await action.commit(types.WEBPART_CALENDARFOLDER_DATA, folderRes);

                let today = IEDate(new Date());

                if (pDailyEvent) {
                    let end = IEDate(today);
                    end.setDate(end.getDate() + 1);

                    await action.dispatch(types.WEBPART_CALENDARDAILYEVENT_DATA, { start: today, end: end, isCommonLoad: true });
                }

                if (pMonthlyEvent) {
                    let daysList = uGetDaysInMonth(today, true, 0, false);
                    let matrixStart = daysList[0];
                    let matrixEnd = daysList[daysList.length - 1];

                    await action.dispatch(types.WEBPART_CALENDARMONTHLYEVENT_DATA, {
                        start: matrixStart,
                        end: matrixEnd,
                    });
                }
            });
        }

        action.commit(types.WEBPART_COMMONDATA_MOUNTED, true);
    },

    [types.WEBPART_CALENDARMONTHLYEVENT_DATA](action, payload: { start: Date; end: Date }): Promise<any> {
        let currentUser = accountStore.state.current?.user ?? null;

        if (currentUser == null) {
            return Promise.resolve();
        }

        let szStart: string = uGetDateFormat("yyyy-MM-dd 00:00:00", payload.start);
        let szEnd: string = uGetDateFormat("yyyy-MM-dd 00:00:00", payload.end);

        let resultArray: CalendarEventResponseData[] = [];
        let funcArr: Promise<any>[] = [];
        let folderIds = action.state.calendarFolders.map((f) => f.id);
        let eventRequest = getEventRequest(payload.start, payload.end, folderIds);

        funcArr.push(calendarApi.getCalendarEvents(eventRequest));
        funcArr.push(
            calendarApi.getCompanyEvents(currentUser.comCode, szStart, szEnd).then((res) => {
                return res.map((e) => ({
                    id: e.holidayId,
                    parentFolderId: "company",
                    appointmentType: "",
                    subject: e.title,
                    location: "한국",
                    start: e.dayStart,
                    end: e.dayStop,
                    importance: "",
                    organizer: {
                        name: "company",
                        address: "company",
                    },
                    isAllDayEvent: true,
                    isCancelled: false,
                    isRecurring: false,
                    isFromMe: false,
                    displayTo: "",
                    scheduleDuration: "종일",
                    iCalRecurrenceId: new Date(),
                    hasAttachments: false,
                }));
            })
        );

        return Promise.all(funcArr).then((resultArr) => {
            let filteredCalendar = resultArr[0].filter((e: CalendarEventResponseData) => {
                var startTIme = IEDate(e.start);
                var endTime = IEDate(e.end);
                var startString = uGetDateFormat("yyyy-MM-dd H:i:s", startTIme);
                var endTimeString = uGetDateFormat("yyyy-MM-dd H:i:s", endTime);

                if (e.isCancelled) {
                    return false;
                } else {
                    return endTimeString !== szStart && startString !== szEnd;
                }
            });

            resultArray.push(...resultArr[1]);
            resultArray.push(...filteredCalendar);

            action.commit(types.WEBPART_CALENDARMONTHLYEVENT_DATA, resultArray);
        });
    },

    [types.WEBPART_CALENDARDAILYEVENT_DATA](action, payload: { start: Date; end: Date; isCommonLoad: false }): Promise<any> {
        let currentUser = accountStore.state.current?.user ?? null;

        if (currentUser == null) {
            return Promise.resolve();
        }

        let szStart: string = uGetDateFormat("yyyy-MM-dd 00:00:00", payload.start);
        let szEnd: string = uGetDateFormat("yyyy-MM-dd 00:00:00", payload.end);

        let resultArray: CalendarEventResponseData[] = [];

        let funcArr: Promise<any>[] = [];

        let folderIds = action.state.calendarFolders.map((f) => f.id);
        let eventRequest = getEventRequest(payload.start, payload.end, folderIds);

        funcArr.push(calendarApi.getCalendarEvents(eventRequest));
        funcArr.push(
            calendarApi.getCompanyEvents(currentUser.comCode, szStart, szEnd).then((res) => {
                return res.map((e) => ({
                    id: e.holidayId,
                    parentFolderId: "company",
                    appointmentType: "",
                    subject: e.title,
                    location: "한국",
                    start: e.dayStart,
                    end: e.dayStop,
                    importance: "",
                    organizer: {
                        name: "company",
                        address: "company",
                    },
                    isAllDayEvent: true,
                    isCancelled: false,
                    isRecurring: false,
                    isFromMe: false,
                    displayTo: "",
                    scheduleDuration: "종일",
                    iCalRecurrenceId: new Date(),
                    hasAttachments: false,
                }));
            })
        );

        return Promise.all(funcArr).then((resultArr) => {
            let filteredCalendar = resultArr[0].filter((e: CalendarEventResponseData) => {
                var startTIme = IEDate(e.start);
                var endTime = IEDate(e.end);
                var startString = uGetDateFormat("yyyy-MM-dd H:i:s", startTIme);
                var endTimeString = uGetDateFormat("yyyy-MM-dd H:i:s", endTime);

                if (e.isCancelled) {
                    return false;
                } else {
                    return endTimeString !== szStart && startString !== szEnd;
                }
            });

            resultArray.push(...resultArr[1]);
            resultArray.push(...filteredCalendar);
            action.commit(types.WEBPART_CALENDARDAILYEVENT_DATA, resultArray);
        });
    },

    [types.WEBPART_UNREADMAIL_DATA](action): Promise<ewsResponse.ewsMail[]> {
        return calendarApi.getUnReadMail().then((res) => {
            action.commit(types.WEBPART_UNREADMAIL_DATA, res);
            return res;
        });
    },
};
